/**
 * Zoom guard for the game screen (Blockari eba3688, ported).
 *
 * iOS Safari has ignored `user-scalable=no` and `maximum-scale` since iOS 10 (an accessibility
 * rule), so a stray two-finger touch pinch-zooms the page, and the board's `touch-action: none`
 * then eats every touch: the player cannot pinch back out. Two layers:
 *
 *  1. Prevent: while a round is on the table, cancel WebKit's `gesture*` events and any
 *     multi-touch `touchstart` / `touchmove` (all non-passive, or preventDefault is a no-op).
 *     Single-finger touches are never touched, and pointer events (the tap that plays a pile)
 *     are unaffected: cancelling a touch event does not cancel its pointer events.
 *  2. Recover: if the page is zoomed anyway (visualViewport.scale > 1.01), briefly pin the
 *     viewport meta to `maximum-scale=1`, which makes Safari snap back to scale 1, then restore
 *     it. If it is still zoomed afterwards, `onStuck(true)` lets the screen show a hint, and the
 *     guard stands down (and the screen relaxes the board's touch-action) until the scale is back
 *     at 1, then re-arms.
 *
 * Attached only while the game screen is up (`ZoomGate`), so every other screen keeps the
 * browser's own pinch-zoom.
 */

/** Above this the page counts as zoomed (visualViewport.scale is 1 at rest; allow float noise). */
export const ZOOMED_SCALE = 1.01;
/** How long the meta stays pinned before it is restored. */
export const PIN_MS = 300;
/** Debounce on visualViewport resize / scroll. */
export const DEBOUNCE_MS = 120;
/** After a restore, wait this long for Safari to settle before judging the reset. */
export const SETTLE_MS = 350;
/** A zoomed page is reset at most this often (the reset itself fires viewport events). */
export const RETRY_MS = 1500;

export interface ViewportLike extends EventTarget {
  readonly scale: number;
}

interface MetaLike {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
}

/** The slice of `document` and `window` the guard touches (a fake in the unit tests). */
export interface ZoomGuardEnv {
  doc: EventTarget & { querySelector(selector: string): MetaLike | null };
  win: { visualViewport?: ViewportLike | null };
}

export interface ZoomGuardOptions {
  /** Called with true when the page is still zoomed after a reset, false once it is back at 1. */
  onStuck?: (stuck: boolean) => void;
  env?: ZoomGuardEnv;
}

export interface ZoomGuard {
  /** Reset now if the page is zoomed (game start / resume), even after an earlier failed try. A no-op at scale 1. */
  check: () => void;
  detach: () => void;
}

const GESTURES = ['gesturestart', 'gesturechange', 'gestureend'] as const;

function multiTouch(e: Event): boolean {
  const t = (e as Partial<TouchEvent>).touches;
  return !!t && t.length > 1;
}

/** The viewport meta's content with `maximum-scale=1` forced in (replacing any existing one). */
export function pinnedContent(content: string): string {
  const parts = content
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p !== '' && !/^maximum-scale\s*=/i.test(p));
  parts.push('maximum-scale=1');
  return parts.join(', ');
}

export function attachZoomGuard(opts: ZoomGuardOptions = {}): ZoomGuard {
  const env: ZoomGuardEnv = opts.env ?? {
    doc: document as unknown as ZoomGuardEnv['doc'],
    win: window,
  };
  const { doc, win } = env;
  const vv = win.visualViewport ?? null;

  // Once a reset has failed the guard stands down: the player is told to pinch out, and the
  // blocking here would be what stops them (the screen relaxes its touch-action to match).
  let stuck = false;
  const block = (e: Event) => {
    if (!stuck && e.cancelable) e.preventDefault();
  };
  const blockMulti = (e: Event) => {
    if (!stuck && multiTouch(e) && e.cancelable) e.preventDefault();
  };
  const passiveOff: AddEventListenerOptions = { passive: false };

  for (const g of GESTURES) doc.addEventListener(g, block, passiveOff);
  doc.addEventListener('touchstart', blockMulti, passiveOff);
  doc.addEventListener('touchmove', blockMulti, passiveOff);

  let detached = false;
  /** The meta's own content while it is pinned (null when it is not). */
  let saved: string | null = null;
  let pinTimer: ReturnType<typeof setTimeout> | undefined;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let lastAttempt = -Infinity;

  const zoomed = () => !!vv && vv.scale > ZOOMED_SCALE;
  const meta = () => doc.querySelector('meta[name="viewport"]');

  const setStuck = (v: boolean) => {
    if (stuck === v) return;
    stuck = v;
    opts.onStuck?.(v);
  };

  const unpin = () => {
    if (pinTimer !== undefined) clearTimeout(pinTimer);
    pinTimer = undefined;
    if (saved === null) return;
    const m = meta();
    if (m) m.setAttribute('content', saved);
    saved = null;
  };

  const reset = () => {
    const m = meta();
    if (!m || saved !== null) return;
    lastAttempt = Date.now();
    saved = m.getAttribute('content') ?? '';
    m.setAttribute('content', pinnedContent(saved));
    pinTimer = setTimeout(() => {
      unpin();
      settleTimer = setTimeout(() => {
        settleTimer = undefined;
        if (!detached) setStuck(zoomed());
      }, SETTLE_MS);
    }, PIN_MS);
  };

  const check = (force: boolean) => {
    if (detached) return;
    if (!zoomed()) {
      setStuck(false);
      return;
    }
    // Pinned or settling: the viewport events are our own doing.
    if (saved !== null || settleTimer !== undefined) return;
    // A failed reset is not retried behind the player's back (it would fight their pinch).
    if (!force && (stuck || Date.now() - lastAttempt < RETRY_MS)) return;
    reset();
  };

  const onViewport = () => {
    if (debounceTimer !== undefined) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      check(false);
    }, DEBOUNCE_MS);
  };
  vv?.addEventListener('resize', onViewport);
  vv?.addEventListener('scroll', onViewport);

  check(true);

  return {
    check: () => check(true),
    detach: () => {
      if (detached) return;
      detached = true;
      for (const g of GESTURES) doc.removeEventListener(g, block, passiveOff);
      doc.removeEventListener('touchstart', blockMulti, passiveOff);
      doc.removeEventListener('touchmove', blockMulti, passiveOff);
      vv?.removeEventListener('resize', onViewport);
      vv?.removeEventListener('scroll', onViewport);
      if (debounceTimer !== undefined) clearTimeout(debounceTimer);
      if (settleTimer !== undefined) clearTimeout(settleTimer);
      if (pinTimer !== undefined) clearTimeout(pinTimer);
      unpin();
      // Leaving the table clears the "stuck" state: the next screen is not the guard's business.
      if (stuck) {
        stuck = false;
        opts.onStuck?.(false);
      }
    },
  };
}

/**
 * Arms the guard while the game screen is up and only then. Called from every screen
 * transition; a repeat of the same state is a no-op, so a pause or resume does not re-attach.
 * (`check()` is what a resume calls: a zoom that crept in behind a dialog is undone before the
 * clock runs again.)
 */
export interface ZoomGate {
  /** The screen changed. `game` arms, anything else disarms. */
  screen: (screen: string) => void;
  /** Reset a zoom now, if the guard is armed. */
  check: () => void;
  readonly armed: boolean;
  destroy: () => void;
}

export function createZoomGate(opts: ZoomGuardOptions = {}): ZoomGate {
  let guard: ZoomGuard | null = null;
  return {
    screen(screen) {
      if (screen === 'game') {
        guard ??= attachZoomGuard(opts);
      } else if (guard) {
        guard.detach();
        guard = null;
      }
    },
    check: () => guard?.check(),
    get armed() {
      return guard !== null;
    },
    destroy() {
      guard?.detach();
      guard = null;
    },
  };
}

/**
 * Where the hint sits while the page is zoomed: the layout viewport is bigger than what is on
 * screen, so it is pinned to the visible part, counter-scaled to a constant size.
 */
export function hintPlacement(vv: {
  scale: number;
  offsetLeft: number;
  offsetTop: number;
  width: number;
  height: number;
}): { left: number; top: number; scale: number } {
  const s = vv.scale > 0 ? vv.scale : 1;
  return {
    left: vv.offsetLeft + vv.width / 2,
    top: vv.offsetTop + vv.height,
    scale: 1 / s,
  };
}
